import i18next from 'i18next';
import { fetch } from '@microsoft/portal-app/lib/fetch';
import { IEnduserEmailPhoneContactsPatch, isUserObject, isCountryCode, isOrgObject } from './models';
import {
  actionContactPreferenceAdminSave,
  actionContactPreferenceAdminSaveSuccess,
  actionContactPreferenceAdminSaveError,
} from '../actions';
import {
  selectContactPreferenceAdminSaveDisabled,
  selectContactPreferenceAdminIsInvalid,
  selectContactPreferenceAdmin,
} from '../selectors';
import { IContactPreferenceAdminSave } from '../models';
import { topicIds } from '../constants';
import { createThunk } from '../../redux';
import { auth } from '../../auth';
import { normalizePhone } from '../utils';
import { settingsManager } from '../../shared/settings';
import { getMSGraphScopeAndEndPoint } from '../../shared/utils';
import { MSGraphScopes } from '../../shared/constants';

export const effectContactPreferenceAdminSave = createThunk(
  async (dispatch, getState, data: IContactPreferenceAdminSave) => {
    if (selectContactPreferenceAdminSaveDisabled(getState())) {
      return;
    }

    const audience = settingsManager.getRequired('cpmAudience');
    const baseUrl = settingsManager.getRequired('cpmBaseUrl');

    dispatch(actionContactPreferenceAdminSave(data));

    if (selectContactPreferenceAdminIsInvalid(getState())) {
      return;
    }

    const { offersOptIn, partnersOptIn, partnersEmail, partnersCountryCode, partnersPhoneNumber } =
      selectContactPreferenceAdmin(getState());

    const user = auth.user;
    const normalPartnersPhoneNumber = normalizePhone(partnersPhoneNumber);
    const normalPartnersEmail = partnersEmail.trim() || user?.email;

    if (!normalPartnersEmail) {
      dispatch(actionContactPreferenceAdminSaveError({ partnersEmailError: 'emailAddressRequired' }));
      return;
    }

    const cultureName = i18next.languages[0] ?? i18next.language ?? 'en-US';
    const token = await auth.acquireToken({ scopes: [audience] });
    const body: IEnduserEmailPhoneContactsPatch = {
      country: await getCountry(),
      email: normalPartnersEmail,
      phoneNumber: normalPartnersPhoneNumber ? `+${partnersCountryCode}${normalPartnersPhoneNumber}` : null,
      emailTopicSettings: [
        {
          cultureName,
          originalSource: auth.clientId,
          topicId: topicIds.offers,
          state: offersOptIn ? 'optInExplicit' : 'optOut',
        },
        {
          cultureName,
          originalSource: auth.clientId,
          topicId: topicIds.partners,
          state: partnersOptIn ? 'optInExplicit' : 'optOut',
        },
      ],
      phoneTopicSettings: [
        {
          cultureName,
          originalSource: auth.clientId,
          topicId: topicIds.partners,
          state: partnersOptIn ? 'optInExplicit' : 'optOut',
        },
      ],
    };
    const response = await fetch(`${baseUrl}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw Error(`error response from PATCH ${response.url} (status: ${response.status})`);
    }

    dispatch(actionContactPreferenceAdminSaveSuccess());
  },
  (error, dispatch) => {
    dispatch(actionContactPreferenceAdminSaveError({ error }));
  },
);

async function getCountry(): Promise<string> {
  const country = (await getUserCountry()) ?? (await getOrgCountry());

  if (!country) {
    throw Error(`unable to determine admin's country`);
  }

  return country;
}

async function getUserCountry(): Promise<string | undefined> {
  const { scope, MSGraphEndPoint } = getMSGraphScopeAndEndPoint(MSGraphScopes.UserReadAll);
  const token = await auth.acquireToken({ scopes: [scope] });
  const response = await fetch(`${MSGraphEndPoint}/v1.0/me?$select=country,usageLocation`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw Error(`error response from GET ${response.url} (status: ${response.status})`);
  }

  const data = await response.json();

  return isUserObject(data) ? [data.usageLocation, data.country].filter(isCountryCode)[0]?.toUpperCase() : undefined;
}

async function getOrgCountry(): Promise<string | undefined> {
  const { scope, MSGraphEndPoint } = getMSGraphScopeAndEndPoint(MSGraphScopes.OrganizationReadAll);
  const token = await auth.acquireToken({ scopes: [scope] });
  const user = auth.user;
  const tenantId = user?.tenantId;
  const response = await fetch(`${MSGraphEndPoint}/v1.0/organization/${tenantId}?$select=country,countryLetterCode`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    throw Error(`error response received when fetching admin's country code (status: ${response.status})`);
  }

  const data = await response.json();

  return isOrgObject(data) ? [data.countryLetterCode, data.country].filter(isCountryCode)[0]?.toUpperCase() : undefined;
}
